import { gql } from '@apollo/client';

/** Write one app_config row. The backend upserts on (key, environment). */
export const UPSERT_APP_CONFIG = gql`
  mutation UpsertAppConfig($objIn: AppConfigUpdateInput!) {
    upsertAppConfig(objIn: $objIn) { success key environment message }
  }
`;

export const DELETE_APP_CONFIG = gql`
  mutation DeleteAppConfig($objIn: AppConfigDeleteInput!) {
    deleteAppConfig(objIn: $objIn) { success message }
  }
`;

/** Re-read app_config into the running process — config on disk is not live. */
export const RELOAD_APP_CONFIG = gql`
  mutation ReloadAppConfig { reloadAppConfig }
`;

export const SEED_APP_CONFIG_FROM_DEFAULTS = gql`
  mutation SeedAppConfigFromDefaults($objIn: AppConfigSeedInput!) {
    seedAppConfigFromDefaults(objIn: $objIn) { success rowsWritten message }
  }
`;

/** A fund house's display name, empanelment and logo. */
export const UPDATE_AMC = gql`
  mutation UpdateAmc($id: Int!, $displayName: String, $isEmpanelled: Boolean, $logoUrl: String) {
    updateAmc(id: $id, displayName: $displayName, isEmpanelled: $isEmpanelled, logoUrl: $logoUrl) {
      success
      message
      amc { id name displayName isEmpanelled logoUrl }
    }
  }
`;

/**
 * Upload an image as a fund house's logo. The file travels as a multipart
 * upload (apollo-upload-client, the app's terminating link); the server stores
 * it and writes its URL to the house's `logoUrl`.
 */
export const UPLOAD_AMC_LOGO = gql`
  mutation UploadAmcLogo($id: Int!, $file: Upload!) {
    uploadAmcLogo(id: $id, file: $file) {
      success
      message
      amc { id name displayName isEmpanelled logoUrl }
    }
  }
`;

/* ── the rationale set the proposal flow offers ─────────────────────────── */

export const CREATE_RATIONALE = gql`
  mutation CreateInvestmentRationale($input: IosRationaleInput!) {
    createInvestmentRationale(input: $input) { success id isActive error }
  }
`;

export const UPDATE_RATIONALE = gql`
  mutation UpdateInvestmentRationale($recordId: Int!, $input: IosRationaleInput!) {
    updateInvestmentRationale(recordId: $recordId, input: $input) { success id isActive error }
  }
`;

export const SET_RATIONALE_ACTIVE = gql`
  mutation SetInvestmentRationaleActive($recordId: Int!, $isActive: Boolean!) {
    setInvestmentRationaleActive(recordId: $recordId, isActive: $isActive) { success id isActive error }
  }
`;

export const REFRESH_RATIONALE_CONFIG = gql`
  mutation RefreshInvestmentRationaleConfig {
    refreshInvestmentRationaleConfig { success rationaleRows awarenessQuestionSets error }
  }
`;

/* ── caches ─────────────────────────────────────────────────────────────── */

/**
 * The only op here that BUILDS rather than drops, and the expensive one.
 * `wait: false` publishes the warm instruction so every API instance warms
 * itself, rather than only the one that answered this call.
 */
export const WARM_CATALOG_CACHE = gql`
  mutation WarmCatalogCache($scope: String!, $wait: Boolean!) {
    warmCatalogCache(scope: $scope, wait: $wait)
  }
`;

export const REFRESH_ASSET_MODEL_CACHE = gql`
  mutation RefreshAssetModelCache { refreshAssetModelCache }
`;

export const REFRESH_MARKET_CONSTANTS_CACHE = gql`
  mutation RefreshMarketConstantsCache {
    refreshMarketConstantsCache { success message }
  }
`;

export const REFRESH_OPTIMIZER_PROFILE_CACHE = gql`
  mutation RefreshOptimizerProfileCache {
    refreshOptimizerProfileCache { success message }
  }
`;

/* ── models ─────────────────────────────────────────────────────────────── */

/**
 * Delete models, cascading to their allocations, variants and metrics rows.
 *
 * `success` is NOT proof a model went: the backend refuses a delete that
 * something still references and reports it as a successful call with the
 * reasons in `blockedBy`. Read `results[].modelDeleted` per model — which is
 * what `lib/deleteAssetModel.js` exists to do.
 *
 * The input takes `modelIds` and nothing else; a single delete is a
 * one-element list.
 */
export const DELETE_ASSET_MODEL_COMPLETE = gql`
  mutation DeleteAssetModelComplete($input: DeleteAssetModelCompleteInput!) {
    deleteAssetModelComplete(input: $input) {
      success
      message
      modelsDeleted
      metricsRowsDeleted
      blockedBy
      results {
        modelId
        success
        message
        modelDeleted
        metricsRowsDeleted
        blockedBy
      }
    }
  }
`;

/**
 * Edit a model.
 *
 * ONE mutation for the whole object: the model's own fields, and — through the
 * `allocationId` / `implementationId` / `subAllocationId` / `securityMasterId`
 * keys — a row of whichever table is being touched. Every field is optional
 * except `modelId`, so a partial input edits exactly what it names.
 *
 * It reports what it touched rather than a bare success, which is the only way
 * to tell "saved" from "matched nothing".
 */
export const UPDATE_ASSET_MODEL_COMPLETE = gql`
  mutation UpdateAssetModelComplete($input: UpdateAssetModelCompleteInput!) {
    updateAssetModelComplete(input: $input) {
      success
      message
      modelId
      modelUpdated
      allocationsUpdated
      implementationsUpdated
      subAllocationsUpdated
      securityMastersUpdated
    }
  }
`;

/**
 * Recompute a model's realized characteristics — what it actually did, from
 * its constituents' history, rather than what it was expected to do.
 *
 * `persist` and `runAllModels` are REQUIRED. app_admin declares `persist`
 * optional and omits `runAllModels` entirely; the live schema takes neither.
 * `persist: false` is a dry run — it computes and reports without writing,
 * which is the safe way to see what would change.
 */
export const RECOMPUTE_REALIZED_CHARACTERISTICS = gql`
  mutation RecomputeRealizedCharacteristics(
    $modelIds: [Int!]
    $variantIndexes: [Int!]
    $versions: [Int!]
    $persist: Boolean!
    $runAllModels: Boolean!
  ) {
    recomputeRealizedCharacteristics(
      modelIds: $modelIds
      variantIndexes: $variantIndexes
      versions: $versions
      persist: $persist
      runAllModels: $runAllModels
    ) {
      success
      message
      computedCount
      skippedCount
      failedCount
      withinBudgetCount
      breachedCount
    }
  }
`;

/**
 * Research a model and write its report.
 *
 * `mode` is REQUIRED and an enum: AGENT runs the research agent now, BATCH
 * queues it. app_admin declares it optional. Answers with a JSON bag rather
 * than a typed payload.
 */
export const RUN_ASSET_MODEL_RESEARCH_AND_REPORT = gql`
  mutation RunAssetModelResearchAndReport($modelIds: [Int!]!, $mode: ResearchMode!) {
    runAssetModelResearchAndReport(modelIds: $modelIds, mode: $mode)
  }
`;
